import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Scale, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, shareLink, timeAgo } from "@/lib/api";
import { Button } from "@/components/ui/button";

export const Side = ({ label, s, testid }) => (
  <section className="surface space-y-2 p-5" data-testid={testid}>
    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
    <p className="text-sm leading-relaxed text-slate-800">{s.summary}</p>
    <ul className="space-y-1.5 text-sm text-slate-600">{s.points.map((p) => <li key={p} className="flex gap-2"><span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-slate-400" />{p}</li>)}</ul>
  </section>
);

const MIDDLE_LISTS = [["facts", "What the record shows"], ["common_ground", "Where both sides agree"], ["trade_offs", "The real trade-offs"], ["unknown", "Still unknown"]];

export const Middle = ({ m, testid }) => (
  <section className="relative space-y-3 rounded-2xl border-2 border-[hsl(var(--facts)/0.35)] bg-[hsl(var(--facts)/0.05)] p-5 shadow-sm" data-testid={testid}>
    <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[hsl(var(--facts))]"><Scale size={14} />Middle's perspective</p>
    <p className="font-serif text-lg leading-snug text-slate-900">{m.summary}</p>
    {MIDDLE_LISTS.filter(([k]) => m[k]?.length).map(([k, l]) => (
      <div key={k}><p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{l}</p>
        <ul className="mt-1 space-y-1 text-sm text-slate-700">{m[k].map((x) => <li key={x}>• {x}</li>)}</ul></div>
    ))}
  </section>
);

const ShareActions = ({ liveId, issueTitle, c }) => {
  const share = async () => {
    try {
      const { data } = await api.post("/share", { kind: "perspective", item_id: liveId, channel: "link" });
      if ((await shareLink(`/api/public/og/perspective/${liveId}?c=${c.code}&ref=${data.ref}`)) === "copied") toast.success("Link copied. Anyone can open it, no account needed");
    } catch { toast.error("Couldn't create a share link"); }
  };
  return (
    <div className="flex flex-wrap gap-2">
      <Button size="sm" variant="outline" className="rounded-full" onClick={share} data-testid={`perspective-share-${c.code}`}><Share2 size={14} className="mr-1.5" />Share</Button>
    </div>
  );
};

export const CountryBlock = ({ c, title, liveId, issueTitle }) => (
  <div className="space-y-3 fade-up" data-testid={`perspective-country-${c.code}`}>
    <div className="flex flex-wrap items-center justify-between gap-2"><h3 className="text-xl">{title ? `${title}: ` : ""}<span className="text-slate-500">{c.name}</span></h3>
      {liveId && <ShareActions liveId={liveId} issueTitle={issueTitle} c={c} />}</div>
    <div className="grid gap-4 lg:grid-cols-[1fr_1.3fr_1fr] lg:items-start">
      <Side label={c.left_label} s={c.left} testid={`perspective-left-${c.code}`} />
      <Middle m={c.middle} testid={`perspective-middle-${c.code}`} />
      <Side label={c.right_label} s={c.right} testid={`perspective-right-${c.code}`} />
    </div>
  </div>
);

const Pending = ({ v, what }) => v.state === "failed"
  ? <p className="surface p-5 text-sm text-slate-500" data-testid="perspective-failed">We couldn't write {what} just now. It will be retried shortly.</p>
  : <p className="surface flex items-center gap-2 p-5 text-sm text-slate-500" data-testid="perspective-generating"><Loader2 size={16} className="animate-spin" />Writing {what} from the verified record. It is prepared in the background and usually appears within a few minutes…</p>;

export const Perspective = ({ liveId, issueTitle }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    let alive = true, timer;
    const load = () => api.get(`/live/${liveId}/perspective`).then((r) => {
      if (!alive) return;
      setD(r.data);
      const waiting = r.data.issue.state === "generating" || r.data.reader?.state === "generating" || (r.data.issue.state === "ready" && r.data.reader_country && !r.data.reader && !r.data.issue.countries.some((c) => c.code === r.data.reader_country.code));
      if (waiting) timer = setTimeout(load, 15000);
    }).catch(() => alive && setD(false));
    load();
    return () => { alive = false; clearTimeout(timer); };
  }, [liveId]);
  if (d === false) return <p className="text-sm text-slate-500">Middle's perspective is unavailable right now.</p>;
  if (!d) return <Pending v={{ state: "generating" }} what="Middle's perspective" />;
  const { issue, reader, reader_country: rc } = d;
  return (
    <div className="space-y-8" data-testid="perspective-panel">
      <p className="max-w-2xl text-sm text-slate-600">How mainstream left- and right-leaning politicians approach this issue, in short. Middle's perspective sits in the middle. It isn't another opinion: it is what the verified record shows, where both sides agree and what is still unknown.</p>
      {issue.state === "ready" ? issue.countries.map((c) => <CountryBlock key={c.code} c={c} title="Where it's happening" liveId={liveId} issueTitle={issueTitle} />) : <Pending v={issue} what="Middle's perspective" />}
      {issue.state === "ready" && rc && !issue.countries.some((c) => c.code === rc.code) && (reader?.state === "ready" ? reader.countries.map((c) => <CountryBlock key={c.code} c={c} title="Where you are" liveId={liveId} issueTitle={issueTitle} />) : <Pending v={reader || { state: "generating" }} what={`the view from ${rc.name || "your country"}`} />)}
      {!rc && <p className="text-sm text-slate-500" data-testid="perspective-set-location">Set your location in <Link to="/settings" className="underline">Settings</Link> to also see how politicians in your country approach this.</p>}
      {issue.state === "ready" && <p className="text-[11px] text-slate-400" data-testid="perspective-disclaimer">Written by AI from Middle's verified record and widely reported party positions. Updated {timeAgo(issue.generated_at)}{issue.refreshing ? ", refreshing now" : ""}. It refreshes as the record changes and may contain mistakes.</p>}
    </div>
  );
};
